import React, { forwardRef } from 'react';
import styles from './ComboBox.module.css';
import {
  Button as AriaButton,
  ComboBox as AriaComboBox,
  ComboBoxProps as AriaComboBoxProps,
  Input as AriaInput,
  Popover as AriaPopover,
  Group as AriaGroup,
  ListBoxItemProps as AriaListBoxItemProps,
} from 'react-aria-components';
import { useObjectRef } from '@react-aria/utils';
import Icon from '../Icon';
import { Field, FieldProps } from '../_base/Field';
import LoadingSpinner from '../LoadingSpinner';

export interface ComboBoxProps<T extends object>
  extends FieldProps<AriaComboBoxProps<T>> {
  isLoading?: boolean;
  hasDropDownButton?: boolean;
  children: React.ReactNode | React.ReactElement<AriaListBoxItemProps<T>>;
}

export const ComboBox = forwardRef(function ComboBox<T extends object>(
  { children, isLoading, hasDropDownButton = true, ...props }: ComboBoxProps<T>,
  forwardedRef: React.ForwardedRef<HTMLInputElement>,
) {
  const inputRef = useObjectRef(forwardedRef);

  return (
    <Field<AriaComboBoxProps<T>>
      as={AriaComboBox}
      className={styles['comboBox']}
      {...props}
    >
      <AriaGroup className={styles['comboBox-group']}>
        <AriaInput className={styles['comboBox-input']} ref={inputRef} />
        {isLoading ? (
          <LoadingSpinner size="1.25x" className={styles['comboBox-loading']} />
        ) : (
          hasDropDownButton && (
            <AriaButton
              className={styles.comboBoxButton}
              isDisabled={props?.isDisabled}
            >
              <Icon
                iconName="angles-up-down"
                iconPrefix="fas"
                padding="roomy"
                scale="1x"
              />
            </AriaButton>
          )
        )}
      </AriaGroup>
      <AriaPopover className={styles['comboBox-popover']}>
        {children}
      </AriaPopover>
    </Field>
  );
});

export default ComboBox;
